import { useCallback, useEffect, useRef, useState } from 'react';

import { ACTIVITY_GRACE_MS, OLLAMA_POLL_MS } from '../config';
import { fetchCapabilities, fetchLoadedModels, fetchModels, fetchVersion } from '../api/ollama';

/**
 * Pollt den geteilten Zustand des Ollama-Servers.
 *
 * Belegt-Erkennung: Ollama setzt `expires_at` eines geladenen Modells bei jeder
 * Anfrage neu (keep_alive). Steigt der Wert zwischen zwei Polls, laeuft gerade
 * eine Anfrage. Genau das ist das Signal fuer "jemand ist dran". Da /api/ps
 * keine Nutzer unterscheidet, meldet die Komponente eigene Aktivitaet ueber
 * `reportSelfActivity`, damit die eigene Generierung nicht als fremde zaehlt.
 */
export default function useOllamaServer() {
    const [state, setState] = useState({
        online: null, // null = noch unbekannt
        version: null,
        models: [],
        loaded: [],
        error: null,
        errorStatus: null, // HTTP-Status des Fehlers, 403 = nicht im Hochschulnetz
        lastUpdated: null,
    });

    // Modellname -> zuletzt gesehener expires_at
    const previousExpiryRef = useRef(new Map());
    // Modellname -> Zeitpunkt der letzten beobachteten Aktivitaet
    const lastActivityRef = useRef(new Map());
    // Eigene laufende Generierung: { model, until }
    const selfActivityRef = useRef(null);
    // Modellname -> Faehigkeiten. Einmal pro Modell abgefragt, nicht bei jedem Poll.
    const capabilitiesRef = useRef(new Map());
    const [tick, setTick] = useState(0);

    const reportSelfActivity = useCallback((model) => {
        selfActivityRef.current = { model, until: Date.now() + ACTIVITY_GRACE_MS };
    }, []);

    const load = useCallback(async (signal) => {
        try {
            const [version, allModels, loaded] = await Promise.all([
                fetchVersion(signal),
                fetchModels(signal),
                fetchLoadedModels(signal),
            ]);

            // Faehigkeiten nur fuer noch unbekannte Modelle nachladen.
            const unknown = allModels.filter((m) => !capabilitiesRef.current.has(m.name));
            if (unknown.length) {
                const results = await Promise.all(
                    unknown.map((m) => fetchCapabilities(m.name, signal))
                );
                unknown.forEach((m, index) => capabilitiesRef.current.set(m.name, results[index]));
            }

            // Reine Embedding-Modelle koennen /api/chat nicht bedienen und
            // gehoeren nicht in die Auswahl. Unbekannte Faehigkeiten (null)
            // bleiben drin, statt sie faelschlich zu verstecken.
            const models = allModels
                .map((m) => ({ ...m, capabilities: capabilitiesRef.current.get(m.name) || null }))
                .filter((m) => !m.capabilities || m.capabilities.includes('completion'));

            const now = Date.now();
            const previous = previousExpiryRef.current;
            const next = new Map();

            loaded.forEach((entry) => {
                const before = previous.get(entry.name);
                // Neu geladen oder Ablaufzeit nach hinten geschoben -> aktive Anfrage.
                if (before === undefined || entry.expiresAt > before) {
                    lastActivityRef.current.set(entry.name, now);
                }
                next.set(entry.name, entry.expiresAt);
            });

            // Entladene Modelle vergessen.
            lastActivityRef.current.forEach((_, name) => {
                if (!next.has(name)) lastActivityRef.current.delete(name);
            });
            previousExpiryRef.current = next;

            setState({
                online: true,
                version,
                models,
                loaded,
                error: null,
                errorStatus: null,
                lastUpdated: now,
            });
        } catch (error) {
            if (error.name === 'AbortError') return;
            previousExpiryRef.current = new Map();
            lastActivityRef.current = new Map();
            setState((current) => ({
                ...current,
                online: false,
                loaded: [],
                error: error.message || 'Ollama nicht erreichbar',
                errorStatus: error.status ?? null,
                lastUpdated: Date.now(),
            }));
        }
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        load(controller.signal);
        const intervalId = setInterval(() => {
            load(controller.signal);
            setTick((value) => value + 1); // Countdown neu rendern
        }, OLLAMA_POLL_MS);

        return () => {
            controller.abort();
            clearInterval(intervalId);
        };
    }, [load]);

    // --- abgeleiteter Belegt-Zustand ---------------------------------------
    const now = Date.now();
    const self = selfActivityRef.current;
    const selfModel = self && self.until > now ? self.model : null;

    const activeModels = [];
    lastActivityRef.current.forEach((at, name) => {
        if (now - at < ACTIVITY_GRACE_MS) activeModels.push(name);
    });

    // Fremde Aktivitaet = aktives Modell, das nicht der eigenen Generierung zuzuordnen ist.
    const foreignModel = activeModels.find((name) => name !== selfModel) || null;
    const foreignEntry = foreignModel ? state.loaded.find((m) => m.name === foreignModel) : null;

    const vramBytes = state.loaded.reduce((sum, entry) => sum + (entry.sizeVram || 0), 0);

    return {
        ...state,
        tick, // erzwingt das Neuzeichnen des Countdowns
        activeModels,
        selfModel,
        busyModel: foreignModel,
        busyExpiresAt: foreignEntry?.expiresAt || 0,
        vramBytes,
        reportSelfActivity,
        refresh: () => load(),
    };
}
